import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useQueryClient } from 'react-query';

import { useAuthUser } from 'auth/hooks';
import {
  EXTRACTOR_VENDOR_MAPPINGS_QUERY_KEY,
  useSaveExtractorVendorMapping,
} from 'companySettings/hooks';
import {
  mapExtractorVendorMappingToForm,
  mapExtractorVendorMappingToSavePayload,
} from 'companySettings/mappers';
import { IExtractorVendorMapping, IExtractorVendorMappingForm } from 'companySettings/types';
import { canRemoveExtractorVendorMapping } from 'companySettings/utils';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';

import { upsertExtractorVendorMappingSchema } from './schema';
import { UpsertExtractorVendorMappingFormFields } from './UpsertExtractorVendorMappingFormFields';
import { RemoveExtractorVendorMappingsButton } from '../RemoveExtractorVendorMappingsButton';

interface IProps {
  extractorVendorMapping?: IExtractorVendorMapping;
  isOpen: boolean;
  onClose: () => void;
}

export const UpsertExtractorVendorMapping = ({
  extractorVendorMapping,
  isOpen,
  onClose,
}: IProps) => {
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const { clearNotification } = useNotificationContext();

  const queryClient = useQueryClient();

  const { user } = useAuthUser();

  // A pending delayed close must not fire after unmount.
  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    []
  );

  // The write only. The remove this drawer also offers is its own button's mutation, whose list
  // refresh has to be deferred — see `onRemoveSuccess` below.
  const { saveExtractorVendorMapping } = useSaveExtractorVendorMapping();

  const defaultValues = useMemo(
    () => mapExtractorVendorMappingToForm(extractorVendorMapping),
    [extractorVendorMapping]
  );

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IExtractorVendorMappingForm>) => {
      clearNotification();
      onClose();
      reset?.(defaultValues);
    },
    [clearNotification, defaultValues, onClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IExtractorVendorMappingForm>) =>
      async (data: IExtractorVendorMappingForm) => {
        try {
          // Both drawer modes write through the one endpoint: the record's id — absent when adding
          // — is what makes the BE create or edit, so it is taken from the record, never the form.
          const response = await saveExtractorVendorMapping(
            mapExtractorVendorMappingToSavePayload(
              { ...data, id: extractorVendorMapping?.id },
              extractorVendorMapping
            )
          );

          if (!response?.success) {
            return;
          }

          // Close BEFORE refreshing the grid, and only then invalidate. On Edit this drawer is
          // rendered by the row's own actions cell, and the refetch replaces that row's node — so
          // refreshing first would unmount the drawer mid-countdown and take the success
          // notification with it.
          closeTimeoutRef.current = setTimeout(() => {
            handleOnClose(reset);
            queryClient.invalidateQueries(EXTRACTOR_VENDOR_MAPPINGS_QUERY_KEY);
          }, 1000);
        } catch {
          // A 4xx rejects the mutation; useHandleErrors has already surfaced the server message in
          // the form notification, and the drawer stays open so the user can correct the input.
        }
      },
    [extractorVendorMapping, handleOnClose, queryClient, saveExtractorVendorMapping]
  );

  // Same ordering as the save path, and for the same reason: the refetch takes the row away — and
  // with it this drawer and its notification — so it may only run once the drawer has closed.
  const onRemoveSuccess = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      handleOnClose();
      queryClient.invalidateQueries(EXTRACTOR_VENDOR_MAPPINGS_QUERY_KEY);
    }, 1000);
  }, [handleOnClose, queryClient]);

  // Edit only: nothing exists to remove while adding, and the gate is the row trash action's own.
  const removeButton = useMemo(() => {
    if (!extractorVendorMapping?.id || !canRemoveExtractorVendorMapping(user)) {
      return null;
    }

    return (
      <RemoveExtractorVendorMappingsButton
        extractorVendorMappings={[extractorVendorMapping]}
        notificationPosition={NotificationPosition.FORM}
        onRemoveSuccess={onRemoveSuccess}
      />
    );
  }, [extractorVendorMapping, onRemoveSuccess, user]);

  return (
    <FormProvider
      schema={upsertExtractorVendorMappingSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertExtractorVendorMappingFormFields
        extractorVendorMapping={extractorVendorMapping}
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        extraButtons={removeButton}
      />
    </FormProvider>
  );
};
